import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  CalendarClock,
  ExternalLink,
  ImagePlus,
  Loader2,
  MoreVertical,
  Pencil,
  Plus,
  RefreshCcw,
  Search,
  Star,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { getSportLogoUrl } from '@low6-pickem/sports-config';
import { MAX_UPLOAD_SIZE_BYTES, MAX_UPLOAD_SIZE_MB } from '@low6-pickem/ui';
import { toast } from 'sonner';
import { buildPublicLobbyUrl, normalizeEnv } from '@/config/public-game-urls';
import { isGameTypeEnabled } from '@/lib/platform-filters';
import { useGetAdminMetaQuery } from '@/api/meta/getMeta';
import {
  useCreateLobbyMutation,
  useCreateCustomGameMutation,
  useUpdateCustomGameMutation,
  useGetTenantCustomGamesQuery,
  useDeleteLobbyMutation,
  useGetTenantLobbiesQuery,
  usePatchLobbyMutation,
  type LobbyGameType,
  type Lobby,
  type LobbyImageType,
  type TenantCustomGame,
} from '@/api/lobbies/getLobbies';
import { type LobbyTemplate, useGetTenantLobbyTemplatesQuery } from '@/api/lobbies/lobbyTemplates';
import { useGetTenantsQuery } from '@/api/tenants/getTenants';
import { useGetTenantImageLibraryQuery } from '@/api/tenants/tenantImageLibrary';
import { uploadImage } from '@/api/upload';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { ContentCard } from '@/components/layout/ContentCard';
import { PageContainer } from '@/components/layout/PageContainer';
import { TenantLobbyAssetSettingsPanel } from '@/components/lobby/TenantLobbyAssetSettingsPanel';
import { TzDateTimeInput } from '@/components/datetime/TzDateTimeInput';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Textarea } from '@/components/ui/textarea';

const LOBBY_TENANT_TABS = ['lobbies', 'custom-games', 'asset-settings'] as const;
export type LobbyTenantTab = (typeof LOBBY_TENANT_TABS)[number];

export const Route = createFileRoute('/dashboard/lobby/$tenantCode/')({
  validateSearch: (search: Record<string, unknown>) => {
    const raw = search.tab as string | undefined;
    if (!raw || !LOBBY_TENANT_TABS.includes(raw as LobbyTenantTab)) {
      return {};
    }
    return { tab: raw as LobbyTenantTab };
  },
  component: TenantLobbiesPage,
});

type LobbyFormLocale = 'en';

const LOCALES: LobbyFormLocale[] = ['en'];
const IMAGE_TYPES: LobbyImageType[] = ['large', 'medium', 'small', 'list_item', 'arcade_header'];

/** Row card sizes aligned with lobby layout sections, plus list item for thumbnails/search. */
const CUSTOM_GAME_DISPLAY_ASSETS: Array<{ value: LobbyImageType; label: string }> = [
  { value: 'medium', label: 'Medium (default)' },
  { value: 'large', label: 'Large' },
  { value: 'small', label: 'Small' },
  { value: 'arcade_header', label: 'Action header' },
  { value: 'list_item', label: 'List item' },
];

type CustomGameAssetSource = 'url' | 'library' | 'device';

type CustomGameAssetFormRow = {
  id: string;
  locale: LobbyFormLocale;
  image_type: LobbyImageType;
  source: CustomGameAssetSource;
  image_url: string;
  uploaded_width?: number;
  uploaded_height?: number;
};

function createEmptyCustomGameAsset(): CustomGameAssetFormRow {
  return {
    id: `cga-${crypto.randomUUID()}`,
    locale: 'en',
    image_type: 'medium',
    source: 'url',
    image_url: '',
  };
}

function coerceLobbyImageType(raw: string): LobbyImageType {
  const v = (raw || '').toLowerCase();
  return IMAGE_TYPES.includes(v as LobbyImageType) ? (v as LobbyImageType) : 'medium';
}

function mapTenantCustomGameToForm(game: TenantCustomGame) {
  return {
    title_en: game.title_en,
    subtitle_en: game.subtitle_en ?? '',
    sport: game.sport,
    game_type: game.game_type,
    external_url: game.external_url,
    custom_entries: game.custom_entries != null ? String(game.custom_entries) : '',
    start_utc: game.start_utc ?? '',
    end_utc: game.end_utc ?? '',
    custom_prize_amount: game.custom_prize_amount != null ? String(game.custom_prize_amount) : '',
    jackpot_enabled: game.jackpot_enabled,
    tags_input: '',
    lobby_tags: [...game.lobby_tags],
    assets:
      game.assets.length > 0
        ? game.assets.map(asset => ({
            id: `loaded-${asset.id}`,
            locale: (LOCALES.includes(asset.locale as LobbyFormLocale)
              ? asset.locale
              : 'en') as LobbyFormLocale,
            image_type: coerceLobbyImageType(asset.image_type),
            source: 'url' as CustomGameAssetSource,
            image_url: asset.image_url,
          }))
        : [createEmptyCustomGameAsset()],
  };
}

const IMAGE_SPECS: Record<
  LobbyImageType,
  { label: string; width: number; height: number; description: string }
> = {
  large: { label: 'Large', width: 361, height: 179, description: 'Primary feature card image' },
  medium: { label: 'Medium', width: 257, height: 144, description: 'Secondary card image' },
  small: { label: 'Small', width: 96, height: 96, description: 'Compact thumbnail image' },
  list_item: { label: 'List Item', width: 96, height: 96, description: 'List row thumbnail image' },
  arcade_header: {
    label: 'Arcade Header',
    width: 361,
    height: 440,
    description: 'Arcade hero header image',
  },
};

type LobbyImageFormSlot = {
  image_url: string;
  uploaded_width?: number;
  uploaded_height?: number;
};

type LobbyImageFormState = Record<LobbyFormLocale, Record<LobbyImageType, LobbyImageFormSlot>>;

type LibraryPickerTarget = {
  configId: string;
  locale: LobbyFormLocale;
  imageType: LobbyImageType;
} | null;

type LobbyConfigFormState = {
  id: string;
  sport: string;
  game_type: string;
  tagInput: string;
  tags: string[];
  images: LobbyImageFormState;
};

// Only offer lobby game types this client deployed (Layer A) — a client without
// Pick'em should never be able to add a Pick'em lobby card.
const GAME_TYPES: LobbyGameType[] = (
  ['pickem', 'squares', 'daily_reveal'] as LobbyGameType[]
).filter(gt => isGameTypeEnabled(gt));
const GAME_TYPE_LABELS: Record<LobbyGameType, string> = {
  pickem: "Pick'em",
  squares: 'Squares',
  daily_reveal: 'Daily Reveal',
};

const SPORT_OPTIONS: Array<{ value: string; label: string; logoCode: string }> = [
  { value: 'nfl', label: 'NFL', logoCode: 'NFL' },
  { value: 'nba', label: 'NBA', logoCode: 'NBA' },
  { value: 'ncaab', label: 'NCAAB', logoCode: 'NCAAMB' },
  { value: 'mlb', label: 'MLB', logoCode: 'MLB' },
  { value: 'pga', label: 'PGA', logoCode: 'PGA' },
  { value: 'nhl', label: 'NHL', logoCode: 'NHL' },
  { value: 'mixed', label: 'Mixed', logoCode: 'MIXED' },
];

function getEmptyImageState(): LobbyImageFormState {
  return {
    en: {
      large: { image_url: '' },
      medium: { image_url: '' },
      small: { image_url: '' },
      list_item: { image_url: '' },
      arcade_header: { image_url: '' },
    },
  };
}

function getDisplayName(blobName: string) {
  const segments = blobName.split('/');
  return segments[segments.length - 1] || blobName;
}

function formatFileSize(bytes: number) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
}

function loadImageDimensions(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Failed to load image dimensions'));
    img.src = url;
  });
}

function validateDimensions(
  imageType: LobbyImageType,
  width: number,
  height: number
): 1 | 2 | null {
  const spec = IMAGE_SPECS[imageType];
  if (width === spec.width && height === spec.height) return 1;
  if (width === spec.width * 2 && height === spec.height * 2) return 2;
  return null;
}

function normalizeTag(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-_]/g, '');
}

function normalizeSport(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_-]/g, '');
}

/** Ensure lobby link has a scheme (admin often pastes example.com without https://). */
function normalizeExternalUrl(raw: string): string {
  const t = raw.trim();
  if (!t) return t;
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

function buildPayloadImages(images: LobbyImageFormState) {
  const payload: Array<{
    locale: LobbyFormLocale;
    image_type: LobbyImageType;
    image_url: string;
    uploaded_width?: number;
    uploaded_height?: number;
  }> = [];

  for (const locale of LOCALES) {
    for (const imageType of IMAGE_TYPES) {
      const slot = images[locale][imageType];
      const imageUrl = slot.image_url.trim();
      if (!imageUrl) continue;
      payload.push({
        locale,
        image_type: imageType,
        image_url: imageUrl,
        uploaded_width: slot.uploaded_width,
        uploaded_height: slot.uploaded_height,
      });
    }
  }

  return payload;
}

function createConfigId() {
  return `cfg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function createEmptyConfig(): LobbyConfigFormState {
  return {
    id: createConfigId(),
    sport: 'nfl',
    game_type: 'pickem',
    tagInput: '',
    tags: [],
    images: getEmptyImageState(),
  };
}

function mapTemplateToConfigs(template: LobbyTemplate): LobbyConfigFormState[] {
  if (template.configs.length > 0) {
    return template.configs.map(config => {
      const nextImages = getEmptyImageState();
      for (const image of config.images) {
        if (image.locale !== 'en' || !IMAGE_TYPES.includes(image.image_type)) continue;
        nextImages.en[image.image_type] = {
          image_url: image.image_url,
          uploaded_width: image.uploaded_width ?? undefined,
          uploaded_height: image.uploaded_height ?? undefined,
        };
      }
      return {
        id: createConfigId(),
        sport: config.sport,
        game_type: config.game_type,
        tagInput: '',
        tags: config.tags,
        images: nextImages,
      };
    });
  }

  const fallbackImages = getEmptyImageState();
  for (const image of template.images) {
    if (image.locale !== 'en' || !IMAGE_TYPES.includes(image.image_type)) continue;
    fallbackImages.en[image.image_type] = {
      image_url: image.image_url,
      uploaded_width: image.uploaded_width ?? undefined,
      uploaded_height: image.uploaded_height ?? undefined,
    };
  }

  return [
    {
      id: createConfigId(),
      sport: template.images[0]?.sport ?? 'all',
      game_type: template.images[0]?.game_type ?? 'pickem',
      tagInput: '',
      tags: template.tags,
      images: fallbackImages,
    },
  ];
}

function TenantLobbiesPage() {
  const { tenantCode } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const activeTab: LobbyTenantTab = search.tab ?? 'lobbies';
  const setActiveTab = (tab: LobbyTenantTab) =>
    navigate({
      to: '/dashboard/lobby/$tenantCode',
      params: { tenantCode },
      search: { tab },
    });
  const fileInputsRef = useRef<Record<string, HTMLInputElement | null>>({});

  const { data: tenants = [] } = useGetTenantsQuery();
  const tenant = tenants.find((t: any) => t.code === tenantCode);

  const { data: lobbies = [], isLoading } = useGetTenantLobbiesQuery(tenant?.tenant_id);
  const { data: templates = [] } = useGetTenantLobbyTemplatesQuery(tenant?.tenant_id);
  const createMutation = useCreateLobbyMutation(tenant?.tenant_id ?? '');
  const createCustomGameMutation = useCreateCustomGameMutation(tenant?.tenant_id ?? '');
  const updateCustomGameMutation = useUpdateCustomGameMutation(tenant?.tenant_id ?? '');
  const deleteMutation = useDeleteLobbyMutation(tenant?.tenant_id ?? '');
  const patchLobbyMutation = usePatchLobbyMutation(tenant?.tenant_id);
  const { data: customGames = [], refetch: refetchTenantCustomGames } =
    useGetTenantCustomGamesQuery(tenant?.tenant_id);
  const { data: meta } = useGetAdminMetaQuery();
  const env = meta?.env ? normalizeEnv(meta.env) : undefined;
  const {
    data: tenantImageLibrary,
    isLoading: isImageLibraryLoading,
    isFetching: isImageLibraryFetching,
    isError: isImageLibraryError,
    refetch: refetchImageLibrary,
  } = useGetTenantImageLibraryQuery(tenantCode);

  const [createOpen, setCreateOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Lobby | null>(null);
  const [activationConfirm, setActivationConfirm] = useState<Lobby | null>(null);
  const [templateSelection, setTemplateSelection] = useState('manual');
  const [form, setForm] = useState({
    slug: '',
    title: '',
    description: '',
    is_default: false,
  });
  const [scheduleActive, setScheduleActive] = useState(false);
  const [scheduleUtc, setScheduleUtc] = useState('');
  const [configs, setConfigs] = useState<LobbyConfigFormState[]>([createEmptyConfig()]);
  const [uploadingSlots, setUploadingSlots] = useState<Set<string>>(new Set());
  const [libraryTarget, setLibraryTarget] = useState<LibraryPickerTarget>(null);
  const [customGameLibraryTarget, setCustomGameLibraryTarget] = useState<{
    assetIndex: number;
    imageType: LobbyImageType;
  } | null>(null);
  const [librarySearch, setLibrarySearch] = useState('');
  const customGameFileInputsRef = useRef<Record<string, HTMLInputElement | null>>({});
  const [customGameAssetUploading, setCustomGameAssetUploading] = useState<Set<string>>(new Set());
  const [editingCustomGameId, setEditingCustomGameId] = useState<string | null>(null);
  const [customGameEditorMode, setCustomGameEditorMode] = useState<'list' | 'edit'>('list');
  const [customGameTableSearch, setCustomGameTableSearch] = useState('');
  const [customGameForm, setCustomGameForm] = useState({
    title_en: '',
    subtitle_en: '',
    sport: '',
    game_type: '',
    external_url: '',
    custom_entries: '',
    start_utc: '',
    end_utc: '',
    custom_prize_amount: '',
    jackpot_enabled: false,
    tags_input: '',
    lobby_tags: [] as string[],
    assets: [createEmptyCustomGameAsset()],
  });

  const filteredCustomGamesTable = useMemo(() => {
    const tokens = customGameTableSearch.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return customGames;
    return customGames.filter(g => {
      const hay = [g.title_en, g.sport, g.game_type, g.external_url, ...(g.lobby_tags ?? [])]
        .join(' ')
        .toLowerCase();
      return tokens.every(t => hay.includes(t));
    });
  }, [customGames, customGameTableSearch]);

  useEffect(() => {
    if (activeTab === 'custom-games') {
      void refetchTenantCustomGames();
    }
  }, [activeTab, refetchTenantCustomGames]);

  const filteredLibraryImages = useMemo(() => {
    const imageList = tenantImageLibrary?.images ?? [];
    const query = librarySearch.trim().toLowerCase();
    if (!query) return imageList;
    return imageList.filter(image => {
      const display = getDisplayName(image.name).toLowerCase();
      return display.includes(query) || image.name.toLowerCase().includes(query);
    });
  }, [librarySearch, tenantImageLibrary?.images]);

  const lobbiesBySlug = useMemo(() => {
    const map = new Map<string, Lobby[]>();
    for (const lobby of lobbies) {
      const list = map.get(lobby.slug) ?? [];
      list.push(lobby);
      map.set(lobby.slug, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [lobbies]);

  if (!tenant) {
    return (
      <PageContainer>
        <p className="text-red-500">Tenant not found: {tenantCode}</p>
      </PageContainer>
    );
  }

  const resetCreateForm = () => {
    setTemplateSelection('manual');
    setForm({
      slug: '',
      title: '',
      description: '',
      is_default: false,
    });
    setConfigs([createEmptyConfig()]);
    setLibraryTarget(null);
    setLibrarySearch('');
    setUploadingSlots(new Set());
    setScheduleActive(false);
    setScheduleUtc('');
  };

  const applyTemplate = (template: LobbyTemplate) => {
    setForm(prev => ({
      ...prev,
      slug: template.slug,
      title: template.title,
      description: template.description ?? '',
    }));
    setConfigs(mapTemplateToConfigs(template));
  };

  const onTemplateSelection = (value: string) => {
    setTemplateSelection(value);
    if (value === 'manual') {
      setConfigs([createEmptyConfig()]);
      return;
    }
    const template = templates.find(item => item.id === value);
    if (!template) return;
    applyTemplate(template);
  };

  const updateConfig = (
    configId: string,
    updater: (current: LobbyConfigFormState) => LobbyConfigFormState
  ) => {
    setConfigs(prev => prev.map(config => (config.id === configId ? updater(config) : config)));
  };

  const addConfig = () => {
    setConfigs(prev => [...prev, createEmptyConfig()]);
  };

  const removeConfig = (configId: string) => {
    setConfigs(prev => {
      if (prev.length <= 1) return prev;
      const next = prev.filter(config => config.id !== configId);
      if (libraryTarget?.configId === configId) {
        setLibraryTarget(null);
        setLibrarySearch('');
      }
      return next.length > 0 ? next : [createEmptyConfig()];
    });
  };

  const addTag = (configId: string, rawValue: string) => {
    const next = normalizeTag(rawValue);
    if (!next) return;

    updateConfig(configId, current => {
      if (current.tags.includes(next)) {
        return { ...current, tagInput: '' };
      }
      return {
        ...current,
        tagInput: '',
        tags: [...current.tags, next],
      };
    });
  };

  const removeTag = (configId: string, tagToRemove: string) => {
    updateConfig(configId, current => ({
      ...current,
      tags: current.tags.filter(tag => tag !== tagToRemove),
    }));
  };

  const setImageSlot = (
    configId: string,
    locale: LobbyFormLocale,
    imageType: LobbyImageType,
    slot: LobbyImageFormSlot
  ) => {
    updateConfig(configId, current => ({
      ...current,
      images: {
        ...current.images,
        [locale]: {
          ...current.images[locale],
          [imageType]: slot,
        },
      },
    }));
  };

  const handleUpload = async (
    configId: string,
    locale: LobbyFormLocale,
    imageType: LobbyImageType,
    file: File
  ) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      toast.error(`Image file size must be less than ${MAX_UPLOAD_SIZE_MB}MB`);
      return;
    }

    const slotKey = `${configId}:${locale}:${imageType}`;
    setUploadingSlots(prev => new Set(prev).add(slotKey));

    try {
      const dataUrl = await fileToDataUrl(file);
      const dimensions = await loadImageDimensions(dataUrl);
      const scale = validateDimensions(imageType, dimensions.width, dimensions.height);
      const spec = IMAGE_SPECS[imageType];
      if (!scale) {
        toast.error(
          `${spec.label} must be ${spec.width}x${spec.height} or ${spec.width * 2}x${spec.height * 2}`
        );
        return;
      }

      const uploadResult = await uploadImage({
        path: `lobbies/${tenantCode}/${locale}`,
        filename: `${form.slug || form.title || 'lobby'}-${imageType}`,
        imageData: dataUrl,
      });

      setImageSlot(configId, locale, imageType, {
        image_url: uploadResult.imageUrl,
        uploaded_width: dimensions.width,
        uploaded_height: dimensions.height,
      });

      toast.success(`${spec.label} image uploaded (${scale}x scale)`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to upload image');
    } finally {
      setUploadingSlots(prev => {
        const next = new Set(prev);
        next.delete(slotKey);
        return next;
      });
    }
  };

  const onFileSelected =
    (configId: string, locale: LobbyFormLocale, imageType: LobbyImageType) =>
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      await handleUpload(configId, locale, imageType, file);
    };

  const onSelectImageFromLibrary = async (url: string) => {
    try {
      if (customGameLibraryTarget) {
        const { assetIndex, imageType } = customGameLibraryTarget;
        const dimensions = await loadImageDimensions(url);
        const scale = validateDimensions(imageType, dimensions.width, dimensions.height);
        const spec = IMAGE_SPECS[imageType];
        if (!scale) {
          toast.error(
            `${spec.label} must be ${spec.width}x${spec.height} or ${spec.width * 2}x${spec.height * 2}`
          );
          return;
        }
        setCustomGameForm(prev => ({
          ...prev,
          assets: prev.assets.map((a, i) =>
            i === assetIndex
              ? {
                  ...a,
                  image_url: url,
                  uploaded_width: dimensions.width,
                  uploaded_height: dimensions.height,
                  source: 'library',
                }
              : a
          ),
        }));
        setCustomGameLibraryTarget(null);
        setLibrarySearch('');
        toast.success(`${spec.label} image selected (${scale}x scale)`);
        return;
      }

      if (!libraryTarget) return;
      const { configId, locale, imageType } = libraryTarget;
      const dimensions = await loadImageDimensions(url);
      const scale = validateDimensions(imageType, dimensions.width, dimensions.height);
      const spec = IMAGE_SPECS[imageType];
      if (!scale) {
        toast.error(
          `${spec.label} must be ${spec.width}x${spec.height} or ${spec.width * 2}x${spec.height * 2}`
        );
        return;
      }

      setImageSlot(configId, locale, imageType, {
        image_url: url,
        uploaded_width: dimensions.width,
        uploaded_height: dimensions.height,
      });
      setLibraryTarget(null);
      setLibrarySearch('');
      toast.success(`${spec.label} image selected (${scale}x scale)`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to select image from library');
    }
  };

  const handleCustomGameUpload = async (assetId: string, imageType: LobbyImageType, file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      toast.error(`Image file size must be less than ${MAX_UPLOAD_SIZE_MB}MB`);
      return;
    }
    setCustomGameAssetUploading(prev => new Set(prev).add(assetId));
    try {
      const dataUrl = await fileToDataUrl(file);
      const dimensions = await loadImageDimensions(dataUrl);
      const scale = validateDimensions(imageType, dimensions.width, dimensions.height);
      const spec = IMAGE_SPECS[imageType];
      if (!scale) {
        toast.error(
          `${spec.label} must be ${spec.width}x${spec.height} or ${spec.width * 2}x${spec.height * 2}`
        );
        return;
      }
      const uploadResult = await uploadImage({
        path: `custom-games/${tenantCode}/en`,
        filename: `${
          customGameForm.title_en.trim() || customGameForm.game_type.trim() || 'custom-game'
        }-${imageType}`,
        imageData: dataUrl,
      });
      setCustomGameForm(prev => ({
        ...prev,
        assets: prev.assets.map(a =>
          a.id === assetId
            ? {
                ...a,
                image_url: uploadResult.imageUrl,
                uploaded_width: dimensions.width,
                uploaded_height: dimensions.height,
                source: 'device',
              }
            : a
        ),
      }));
      toast.success(`${spec.label} uploaded (${scale}x scale)`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to upload image');
    } finally {
      setCustomGameAssetUploading(prev => {
        const next = new Set(prev);
        next.delete(assetId);
        return next;
      });
    }
  };

  const onCustomGameFileSelected =
    (assetId: string, imageType: LobbyImageType) =>
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      await handleCustomGameUpload(assetId, imageType, file);
    };

  const handleCreate = async () => {
    if (!tenant?.tenant_id) return;

    if (!form.slug.trim() || !form.title.trim()) {
      toast.error('Slug and title are required');
      return;
    }

    const nonEmptyConfigs = configs
      .map(config => ({
        ...config,
        sport: normalizeSport(config.sport),
      }))
      .filter(
        config =>
          Boolean(config.sport) ||
          config.tags.length > 0 ||
          buildPayloadImages(config.images).length > 0
      );

    if (nonEmptyConfigs.length === 0) {
      toast.error('Add at least one sport + type config with tags or images');
      return;
    }

    for (const config of nonEmptyConfigs) {
      if (!config.sport) {
        toast.error('Each config requires a sport selection');
        return;
      }
    }

    const uniqueCombos = new Set<string>();
    for (const config of nonEmptyConfigs) {
      const combo = `${config.sport}:${config.game_type}`;
      if (uniqueCombos.has(combo)) {
        toast.error(
          `Duplicate config: ${config.sport} / ${GAME_TYPE_LABELS[config.game_type as LobbyGameType] ?? config.game_type}`
        );
        return;
      }
      uniqueCombos.add(combo);
    }

    if (scheduleActive && !scheduleUtc.trim()) {
      toast.error(
        'Choose a date and time for scheduled activation, or turn off “Schedule active”.'
      );
      return;
    }

    try {
      const useSchedule = scheduleActive && !!scheduleUtc.trim();
      const created: any = await createMutation.mutateAsync({
        slug: form.slug.trim().toLowerCase(),
        title: form.title.trim(),
        description: form.description.trim() || null,
        is_default: form.is_default,
        is_active: useSchedule ? false : true,
        scheduled_active_at: useSchedule ? scheduleUtc.trim() : undefined,
        configs: nonEmptyConfigs.map(config => ({
          sport: config.sport,
          game_type: config.game_type,
          tags: config.tags,
          images: buildPayloadImages(config.images),
        })),
        source_template_id: templateSelection !== 'manual' ? templateSelection : null,
      });

      setCreateOpen(false);
      resetCreateForm();

      if (created?.lobby?.id) {
        navigate({
          to: '/dashboard/lobby/$tenantCode/$lobbyId',
          params: { tenantCode, lobbyId: created.lobby.id },
        });
      }
    } catch (error) {
      toast.error((error as { message?: string })?.message || 'Failed to create lobby');
    }
  };

  const resetCustomGameForm = () => {
    setEditingCustomGameId(null);
    setCustomGameForm({
      title_en: '',
      subtitle_en: '',
      sport: '',
      game_type: '',
      external_url: '',
      custom_entries: '',
      start_utc: '',
      end_utc: '',
      custom_prize_amount: '',
      jackpot_enabled: false,
      tags_input: '',
      lobby_tags: [],
      assets: [createEmptyCustomGameAsset()],
    });
    setCustomGameLibraryTarget(null);
    customGameFileInputsRef.current = {};
    setCustomGameAssetUploading(new Set());
  };

  const goToCustomGameList = () => {
    resetCustomGameForm();
    setCustomGameEditorMode('list');
  };

  const openNewCustomGame = () => {
    resetCustomGameForm();
    setCustomGameEditorMode('edit');
  };

  const openEditCustomGame = (game: TenantCustomGame) => {
    setEditingCustomGameId(game.id);
    setCustomGameForm(mapTenantCustomGameToForm(game));
    setCustomGameLibraryTarget(null);
    customGameFileInputsRef.current = {};
    setCustomGameAssetUploading(new Set());
    setCustomGameEditorMode('edit');
  };

  const normalizeCustomType = (value: string) =>
    value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_-]/g, '');

  const addCustomGameTag = () => {
    const next = normalizeTag(customGameForm.tags_input);
    if (!next || customGameForm.lobby_tags.includes(next)) return;
    setCustomGameForm(prev => ({
      ...prev,
      tags_input: '',
      lobby_tags: [...prev.lobby_tags, next],
    }));
  };

  const removeCustomGameTag = (tag: string) => {
    setCustomGameForm(prev => ({ ...prev, lobby_tags: prev.lobby_tags.filter(t => t !== tag) }));
  };

  const handleSaveCustomGame = async () => {
    if (
      !customGameForm.title_en.trim() ||
      !customGameForm.sport.trim() ||
      !customGameForm.game_type.trim() ||
      !customGameForm.external_url.trim()
    ) {
      toast.error('Title, sport, game type, and URL are required');
      return;
    }
    const body = {
      title_en: customGameForm.title_en.trim(),
      subtitle_en: customGameForm.subtitle_en.trim() || null,
      sport: normalizeSport(customGameForm.sport),
      game_type: normalizeCustomType(customGameForm.game_type),
      external_url: normalizeExternalUrl(customGameForm.external_url),
      custom_entries: (() => {
        const s = customGameForm.custom_entries;
        if (s === '') return null;
        const n = Math.trunc(Number(s));
        return Number.isFinite(n) && n >= 0 ? n : null;
      })(),
      start_utc: customGameForm.start_utc?.trim() ? customGameForm.start_utc : null,
      end_utc: customGameForm.end_utc?.trim() ? customGameForm.end_utc : null,
      custom_prize_amount: (() => {
        const s = customGameForm.custom_prize_amount;
        if (s === '') return null;
        const n = Math.trunc(Number(s));
        return Number.isFinite(n) && n >= 0 ? n : null;
      })(),
      jackpot_enabled: customGameForm.jackpot_enabled,
      lobby_tags: customGameForm.lobby_tags,
      assets: customGameForm.assets
        .filter(asset => asset.image_url.trim().length > 0)
        .map(asset => ({
          locale: asset.locale || 'en',
          image_type: asset.image_type,
          image_url: asset.image_url.trim(),
        })),
    };
    try {
      if (editingCustomGameId) {
        await updateCustomGameMutation.mutateAsync({ customGameId: editingCustomGameId, body });
        toast.success('Custom game updated');
      } else {
        await createCustomGameMutation.mutateAsync(body);
        toast.success('Custom game created');
      }
      await refetchTenantCustomGames();
      goToCustomGameList();
    } catch (error) {
      toast.error((error as { message?: string })?.message || 'Failed to save custom game');
    }
  };

  const isSavingCustomGame =
    createCustomGameMutation.isPending || updateCustomGameMutation.isPending;

  return (
    <TooltipProvider>
      <PageContainer>
        <Breadcrumbs overrides={{ lobby: 'Lobby', [tenantCode]: tenant.title || tenantCode }} />

        <div className="mb-4">
          <h1 className="text-2xl font-semibold">{tenant.title} — Lobbies</h1>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={value => setActiveTab(value as LobbyTenantTab)}
          className="w-full"
        >
          <TabsList className="mb-6 h-auto flex-wrap justify-start gap-1">
            <TabsTrigger value="lobbies">Lobbies</TabsTrigger>
            <TabsTrigger value="custom-games">Custom Games</TabsTrigger>
            <TabsTrigger value="asset-settings">Asset Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="lobbies" className="mt-0 space-y-4">
            <div className="flex justify-end">
              <Button
                className="bg-purple-600 hover:bg-purple-700 text-white"
                onClick={() => setCreateOpen(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Create Lobby
              </Button>
            </div>

            <ContentCard>
              {isLoading ? (
                <Table>
                  <TableHeader className="[&_tr]:border-b-0">
                    <TableRow>
                      <TableHead className="bg-gray-100 dark:bg-gray-800 rounded-l-xl font-bold">
                        Title
                      </TableHead>
                      <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                        Default
                      </TableHead>
                      <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                        Active
                      </TableHead>
                      <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                        Scheduled
                      </TableHead>
                      <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                        Created
                      </TableHead>
                      <TableHead className="text-right bg-gray-100 dark:bg-gray-800 rounded-r-xl font-bold">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 6 }).map((__, j) => (
                          <TableCell key={j}>
                            <Skeleton className="h-4 w-24" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : lobbies.length === 0 ? (
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No lobbies yet — click "Create Lobby" to add one.
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              ) : (
                <div className="space-y-8">
                  {lobbiesBySlug.map(([slug, rows]) => (
                    <div key={slug} className="space-y-2">
                      <div className="flex flex-wrap items-baseline gap-2 text-sm text-muted-foreground">
                        <span className="font-semibold text-foreground">Slug</span>
                        <span className="font-mono">{slug}</span>
                        <span className="text-xs">
                          {rows.length} version{rows.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      <Table>
                        <TableHeader className="[&_tr]:border-b-0">
                          <TableRow>
                            <TableHead className="bg-gray-100 dark:bg-gray-800 rounded-l-xl font-bold">
                              Title
                            </TableHead>
                            <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                              Default
                            </TableHead>
                            <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                              Active
                            </TableHead>
                            <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                              Scheduled
                            </TableHead>
                            <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                              Created
                            </TableHead>
                            <TableHead className="text-right bg-gray-100 dark:bg-gray-800 rounded-r-xl font-bold">
                              Actions
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {rows.map((lobby: Lobby) => (
                            <TableRow key={lobby.id}>
                              <TableCell className="font-medium">
                                <Link
                                  to="/dashboard/lobby/$tenantCode/$lobbyId"
                                  params={{ tenantCode, lobbyId: lobby.id }}
                                  className="hover:underline"
                                >
                                  {lobby.title}
                                </Link>
                              </TableCell>
                              <TableCell>
                                {lobby.is_default ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-500" />
                                    </TooltipTrigger>
                                    <TooltipContent>Default lobby for this tenant</TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Switch
                                  checked={lobby.is_active}
                                  disabled={patchLobbyMutation.isPending}
                                  onCheckedChange={async next => {
                                    if (next) {
                                      setActivationConfirm(lobby);
                                      return;
                                    }
                                    try {
                                      await patchLobbyMutation.mutateAsync({
                                        lobbyId: lobby.id,
                                        body: { is_active: false },
                                      });
                                      toast.success('Lobby set to inactive');
                                    } catch (error) {
                                      toast.error(
                                        (error as { message?: string })?.message ??
                                          'Failed to set lobby inactive'
                                      );
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell className="text-sm text-gray-600 max-w-[200px]">
                                {lobby.scheduled_active_at ? (
                                  <span title={lobby.scheduled_active_at}>
                                    {new Date(lobby.scheduled_active_at).toLocaleString()}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </TableCell>
                              <TableCell className="text-sm text-gray-600">
                                {new Date(lobby.created_at).toLocaleDateString()}
                              </TableCell>
                              <TableCell className="text-right">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() =>
                                        navigate({
                                          to: '/dashboard/lobby/$tenantCode/$lobbyId',
                                          params: { tenantCode, lobbyId: lobby.id },
                                        })
                                      }
                                    >
                                      <Pencil className="mr-2 h-4 w-4" />
                                      Edit
                                    </DropdownMenuItem>
                                    {(() => {
                                      // null when this client deploys no lobby app in this
                                      // environment. Disabled rather than hidden, with the
                                      // reason in the tooltip: a missing menu item reads as a
                                      // bug, and the usual cause is the backend's ENV app
                                      // setting being unset, which is worth surfacing.
                                      const publicUrl = buildPublicLobbyUrl(
                                        tenantCode,
                                        lobby.slug,
                                        env
                                      );
                                      return (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <DropdownMenuItem
                                              disabled={!publicUrl}
                                              onClick={() => {
                                                if (!publicUrl) return;
                                                window.open(
                                                  publicUrl,
                                                  '_blank',
                                                  'noopener,noreferrer'
                                                );
                                              }}
                                            >
                                              <ExternalLink className="mr-2 h-4 w-4" />
                                              Open public URL
                                            </DropdownMenuItem>
                                          </TooltipTrigger>
                                          <TooltipContent side="left">
                                            {publicUrl
                                              ? `Open ${publicUrl} in a new tab`
                                              : 'No lobby origin is declared for this environment in platform.config.json'}
                                          </TooltipContent>
                                        </Tooltip>
                                      );
                                    })()}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => setToDelete(lobby)}
                                      className="text-red-600 focus:text-red-600"
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ))}
                </div>
              )}
            </ContentCard>
          </TabsContent>

          <Dialog
            open={createOpen}
            onOpenChange={open => {
              setCreateOpen(open);
              if (!open) resetCreateForm();
            }}
          >
            <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Lobby</DialogTitle>
                <DialogDescription>
                  Create a lobby manually or pick a template to auto-populate fields.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="lobby-template">Lobby template</Label>
                  <Select value={templateSelection} onValueChange={onTemplateSelection}>
                    <SelectTrigger id="lobby-template">
                      <SelectValue placeholder="Manual setup" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual (No Template)</SelectItem>
                      {templates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Select a template to prefill fields and link this lobby to that asset template.
                    Future template updates in Asset Settings will sync here and on the public
                    lobby.
                  </p>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="slug">Slug</Label>
                    <Input
                      id="slug"
                      placeholder="default"
                      value={form.slug}
                      onChange={e => setForm(prev => ({ ...prev, slug: e.target.value }))}
                    />
                    <p className="text-xs text-muted-foreground">
                      Lowercase letters, numbers, hyphen, underscore.
                    </p>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      placeholder="Main Lobby"
                      value={form.title}
                      onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (optional)</Label>
                  <Textarea
                    id="description"
                    value={form.description}
                    onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Lobby description"
                  />
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.is_default}
                    onChange={e => setForm(prev => ({ ...prev, is_default: e.target.checked }))}
                  />
                  Make this the default lobby for {tenant.title}
                </label>

                <div className="rounded-lg border p-4 space-y-3 bg-muted/30">
                  <div className="flex items-center gap-2">
                    <CalendarClock className="h-4 w-4 text-muted-foreground" aria-hidden />
                    <span className="font-medium">Schedule active (optional)</span>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={scheduleActive}
                      onChange={e => {
                        const on = e.target.checked;
                        setScheduleActive(on);
                        if (!on) setScheduleUtc('');
                      }}
                    />
                    Schedule when this lobby becomes live for this slug
                  </label>
                  {scheduleActive ? (
                    <TzDateTimeInput
                      label="Go live at"
                      id="lobby-schedule-create"
                      value={scheduleUtc}
                      onChange={v => setScheduleUtc(v)}
                    />
                  ) : null}
                  <p className="text-xs text-muted-foreground">
                    When scheduled, the lobby is created inactive until this time. The server
                    activates it automatically and deactivates other versions with the same slug.
                    You cannot save with this option checked unless a date and time are set. Two
                    versions cannot share the same scheduled instant.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-base font-semibold">Sport + Type Config</h3>
                    <Button type="button" variant="outline" onClick={addConfig}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Sport + Type Config
                    </Button>
                  </div>

                  {configs.map((config, configIndex) => (
                    <div key={config.id} className="rounded-lg border p-4 space-y-4 bg-card">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium">Config {configIndex + 1}</p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={configs.length <= 1}
                          onClick={() => removeConfig(config.id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Remove
                        </Button>
                      </div>

                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Sport</Label>
                          <Select
                            value={config.sport}
                            onValueChange={value =>
                              updateConfig(config.id, current => ({
                                ...current,
                                sport: value,
                              }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select sport" />
                            </SelectTrigger>
                            <SelectContent>
                              {SPORT_OPTIONS.map(option => (
                                <SelectItem key={option.value} value={option.value}>
                                  <div className="flex items-center gap-2">
                                    <img
                                      src={getSportLogoUrl(option.logoCode)}
                                      alt={option.label}
                                      className="w-6 h-6 object-contain"
                                    />
                                    <span>{option.label}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Game Type</Label>
                          <Select
                            value={config.game_type}
                            onValueChange={value =>
                              updateConfig(config.id, current => ({
                                ...current,
                                game_type: value,
                              }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select game type" />
                            </SelectTrigger>
                            <SelectContent>
                              {GAME_TYPES.map(gameType => (
                                <SelectItem key={gameType} value={gameType}>
                                  {GAME_TYPE_LABELS[gameType]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Tags</Label>
                        <div className="flex gap-2">
                          <Input
                            placeholder="Type tag and press Enter"
                            value={config.tagInput}
                            onChange={e =>
                              updateConfig(config.id, current => ({
                                ...current,
                                tagInput: e.target.value,
                              }))
                            }
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ',') {
                                e.preventDefault();
                                addTag(config.id, config.tagInput);
                              }
                            }}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => addTag(config.id, config.tagInput)}
                          >
                            Add Tag
                          </Button>
                        </div>
                        {config.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {config.tags.map(tag => (
                              <Badge
                                key={`${config.id}-${tag}`}
                                variant="secondary"
                                className="flex items-center gap-1"
                              >
                                {tag}
                                <button
                                  type="button"
                                  className="rounded-sm hover:bg-black/10 p-0.5"
                                  aria-label={`Remove ${tag} tag`}
                                  onClick={() => removeTag(config.id, tag)}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold">Images</h4>
                        <p className="text-xs text-muted-foreground">English</p>
                        {LOCALES.map(locale => (
                          <div
                            key={`${config.id}-${locale}`}
                            className="grid gap-4 md:grid-cols-2 xl:grid-cols-5 mt-2"
                          >
                            {IMAGE_TYPES.map(imageType => {
                              const spec = IMAGE_SPECS[imageType];
                              const slot = config.images[locale][imageType];
                              const slotKey = `${config.id}:${locale}:${imageType}`;
                              const isUploading = uploadingSlots.has(slotKey);
                              return (
                                <div
                                  key={slotKey}
                                  className="rounded-lg border p-3 bg-background space-y-3"
                                >
                                  <div>
                                    <p className="font-medium">{spec.label}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {spec.description}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {spec.width}x{spec.height} or {spec.width * 2}x
                                      {spec.height * 2}
                                    </p>
                                  </div>

                                  <div className="h-28 rounded border bg-muted/20 overflow-hidden">
                                    {slot.image_url ? (
                                      <img
                                        src={slot.image_url}
                                        alt={`${spec.label} preview`}
                                        className="h-full w-full object-contain"
                                      />
                                    ) : (
                                      <div className="h-full w-full flex items-center justify-center text-xs text-muted-foreground">
                                        No image
                                      </div>
                                    )}
                                  </div>

                                  <div className="flex gap-2">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      disabled={isUploading}
                                      onClick={() => fileInputsRef.current[slotKey]?.click()}
                                    >
                                      {isUploading ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Upload className="h-4 w-4" />
                                      )}
                                    </Button>
                                    <input
                                      ref={el => {
                                        fileInputsRef.current[slotKey] = el;
                                      }}
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={onFileSelected(config.id, locale, imageType)}
                                    />

                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        setLibraryTarget({ configId: config.id, locale, imageType })
                                      }
                                      title="Select from tenant image library"
                                    >
                                      <ImagePlus className="h-4 w-4" />
                                    </Button>

                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      disabled={!slot.image_url}
                                      onClick={() =>
                                        setImageSlot(config.id, locale, imageType, {
                                          image_url: '',
                                        })
                                      }
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={!form.slug.trim() || !form.title.trim() || createMutation.isPending}
                >
                  {createMutation.isPending ? 'Creating...' : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <TabsContent value="custom-games" className="mt-0 space-y-6">
            {customGameEditorMode === 'list' ? (
              <ContentCard>
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">Saved custom games</h2>
                    <p className="text-sm text-muted-foreground">
                      External contests for lobby section filters. Click a row or Edit to open the
                      form, or create a new game.
                    </p>
                  </div>
                  <Button
                    className="shrink-0 bg-purple-600 text-white hover:bg-purple-700"
                    type="button"
                    onClick={openNewCustomGame}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    New custom game
                  </Button>
                </div>
                <div className="relative mb-4 max-w-md">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className="pl-9"
                    value={customGameTableSearch}
                    onChange={e => setCustomGameTableSearch(e.target.value)}
                    placeholder="Filter by title, sport, type, URL, tags…"
                    autoComplete="off"
                  />
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Sport</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Tags</TableHead>
                      <TableHead className="text-right">Jackpot</TableHead>
                      <TableHead className="text-right w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCustomGamesTable.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground">
                          {customGames.length === 0
                            ? 'No custom games yet — click New custom game to add one.'
                            : 'No games match your search.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCustomGamesTable.map(g => (
                        <TableRow
                          key={g.id}
                          className="cursor-pointer hover:bg-muted/40"
                          onClick={() => openEditCustomGame(g)}
                        >
                          <TableCell className="font-medium">{g.title_en}</TableCell>
                          <TableCell>{g.sport}</TableCell>
                          <TableCell className="font-mono text-sm">{g.game_type}</TableCell>
                          <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                            {(g.lobby_tags ?? []).join(', ') || '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            {g.jackpot_enabled ? (
                              <Badge>Yes</Badge>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              title="Edit custom game"
                              onClick={e => {
                                e.stopPropagation();
                                openEditCustomGame(g);
                              }}
                            >
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ContentCard>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-3 border-b pb-4">
                  <Button type="button" variant="outline" size="sm" onClick={goToCustomGameList}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to saved custom games
                  </Button>
                  <h2 className="text-lg font-semibold">
                    {editingCustomGameId ? 'Edit custom game' : 'New custom game'}
                    {customGameForm.title_en.trim() ? (
                      <span className="ml-2 font-normal text-muted-foreground">
                        {' '}
                        — {customGameForm.title_en}
                      </span>
                    ) : null}
                  </h2>
                </div>

                <ContentCard>
                  <p className="mb-4 text-sm text-muted-foreground">
                    {editingCustomGameId
                      ? 'Update this saved external game — Save replaces its data and asset rows (PUT).'
                      : 'Add an external game for lobby section source filters and dynamic rules (POST on save).'}
                  </p>

                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Game title</Label>
                        <Input
                          value={customGameForm.title_en}
                          onChange={e =>
                            setCustomGameForm(prev => ({ ...prev, title_en: e.target.value }))
                          }
                          placeholder="External contest name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Game subtitle</Label>
                        <Input
                          value={customGameForm.subtitle_en}
                          onChange={e =>
                            setCustomGameForm(prev => ({ ...prev, subtitle_en: e.target.value }))
                          }
                          placeholder="Optional subtitle"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Sport</Label>
                        <Input
                          value={customGameForm.sport}
                          onChange={e =>
                            setCustomGameForm(prev => ({ ...prev, sport: e.target.value }))
                          }
                          placeholder="nfl, nba, mixed, etc"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Game type</Label>
                        <Input
                          value={customGameForm.game_type}
                          onChange={e =>
                            setCustomGameForm(prev => ({ ...prev, game_type: e.target.value }))
                          }
                          placeholder="custom type"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>URL</Label>
                        <Input
                          value={customGameForm.external_url}
                          onChange={e =>
                            setCustomGameForm(prev => ({ ...prev, external_url: e.target.value }))
                          }
                          placeholder="https://..."
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Entries</Label>
                        <Input
                          type="number"
                          min={0}
                          value={customGameForm.custom_entries}
                          onChange={e =>
                            setCustomGameForm(prev => ({ ...prev, custom_entries: e.target.value }))
                          }
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Prize amount</Label>
                        <Input
                          type="number"
                          min={0}
                          value={customGameForm.custom_prize_amount}
                          onChange={e =>
                            setCustomGameForm(prev => ({
                              ...prev,
                              custom_prize_amount: e.target.value,
                            }))
                          }
                          placeholder="1000"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <TzDateTimeInput
                        label="Start date & time"
                        id="custom-game-start-utc"
                        value={customGameForm.start_utc || undefined}
                        onChange={utcValue =>
                          setCustomGameForm(prev => ({ ...prev, start_utc: utcValue }))
                        }
                        description="Stored in UTC; shown in your sidebar timezone. Optional window start for this listing."
                      />
                      <TzDateTimeInput
                        label="End date & time"
                        id="custom-game-end-utc"
                        value={customGameForm.end_utc || undefined}
                        onChange={utcValue =>
                          setCustomGameForm(prev => ({ ...prev, end_utc: utcValue }))
                        }
                        description="Stored in UTC; optional window end for this listing."
                      />
                    </div>

                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={customGameForm.jackpot_enabled}
                        onChange={e =>
                          setCustomGameForm(prev => ({
                            ...prev,
                            jackpot_enabled: e.target.checked,
                          }))
                        }
                      />
                      Jackpot game
                    </label>

                    <div className="space-y-2">
                      <Label>Tags</Label>
                      <div className="flex gap-2">
                        <Input
                          value={customGameForm.tags_input}
                          onChange={e =>
                            setCustomGameForm(prev => ({ ...prev, tags_input: e.target.value }))
                          }
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ',') {
                              e.preventDefault();
                              addCustomGameTag();
                            }
                          }}
                          placeholder="Type tag and press Enter"
                        />
                        <Button type="button" variant="outline" onClick={addCustomGameTag}>
                          Add Tag
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {customGameForm.lobby_tags.map(tag => (
                          <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                            {tag}
                            <button type="button" onClick={() => removeCustomGameTag(tag)}>
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="space-y-1">
                          <Label>Assets</Label>
                          <p className="text-xs text-muted-foreground">
                            Each asset is one display slot with the same size options as lobby
                            layout rows ( Medium, Large, Small, Action header, plus List item).
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() =>
                            setCustomGameForm(prev => ({
                              ...prev,
                              assets: [...prev.assets, createEmptyCustomGameAsset()],
                            }))
                          }
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Add Asset
                        </Button>
                      </div>
                      {customGameForm.assets.map((asset, index) => {
                        const isUploading = customGameAssetUploading.has(asset.id);
                        const spec = IMAGE_SPECS[asset.image_type];
                        return (
                          <div key={asset.id} className="rounded-lg border p-4 space-y-3 bg-card">
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                              <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 flex-1">
                                <div className="space-y-2">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Label className="cursor-help">Display asset type</Label>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                      Same sizes as lobby section rows. List item is for list/search
                                      thumbnails.
                                    </TooltipContent>
                                  </Tooltip>
                                  <Select
                                    value={asset.image_type}
                                    onValueChange={v =>
                                      setCustomGameForm(prev => ({
                                        ...prev,
                                        assets: prev.assets.map((a, i) =>
                                          i === index
                                            ? { ...a, image_type: v as LobbyImageType }
                                            : a
                                        ),
                                      }))
                                    }
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder="Choose size" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {CUSTOM_GAME_DISPLAY_ASSETS.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Label className="cursor-help">Locale</Label>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Language code for this image slot (matches lobby configs).
                                    </TooltipContent>
                                  </Tooltip>
                                  <Select
                                    value={asset.locale}
                                    onValueChange={v =>
                                      setCustomGameForm(prev => ({
                                        ...prev,
                                        assets: prev.assets.map((a, i) =>
                                          i === index ? { ...a, locale: v as LobbyFormLocale } : a
                                        ),
                                      }))
                                    }
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {LOCALES.map(loc => (
                                        <SelectItem key={loc} value={loc}>
                                          {loc.toUpperCase()}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2 md:col-span-1 sm:col-span-2">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Label className="cursor-help">Image source</Label>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                      Paste a URL, pick from your tenant library, or upload from
                                      this computer. Images must match {spec.width}×{spec.height} or
                                      2× (retina).
                                    </TooltipContent>
                                  </Tooltip>
                                  <Select
                                    value={asset.source}
                                    onValueChange={v =>
                                      setCustomGameForm(prev => ({
                                        ...prev,
                                        assets: prev.assets.map((a, i) =>
                                          i === index
                                            ? { ...a, source: v as CustomGameAssetSource }
                                            : a
                                        ),
                                      }))
                                    }
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="url">Image URL</SelectItem>
                                      <SelectItem value="library">Tenant image library</SelectItem>
                                      <SelectItem value="device">Upload from device</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="shrink-0"
                                disabled={customGameForm.assets.length <= 1}
                                onClick={() =>
                                  setCustomGameForm(prev => ({
                                    ...prev,
                                    assets: prev.assets.filter((_, i) => i !== index),
                                  }))
                                }
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>

                            {asset.source === 'url' && (
                              <div className="space-y-1">
                                <Label className="text-xs">Image URL</Label>
                                <Input
                                  value={asset.image_url}
                                  onChange={e =>
                                    setCustomGameForm(prev => ({
                                      ...prev,
                                      assets: prev.assets.map((a, i) =>
                                        i === index ? { ...a, image_url: e.target.value } : a
                                      ),
                                    }))
                                  }
                                  placeholder="https://..."
                                />
                              </div>
                            )}
                            {asset.source === 'library' && (
                              <div className="flex flex-wrap items-center gap-2">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      onClick={() =>
                                        setCustomGameLibraryTarget({
                                          assetIndex: index,
                                          imageType: asset.image_type,
                                        })
                                      }
                                    >
                                      <ImagePlus className="mr-2 h-4 w-4" />
                                      Browse tenant image library
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Open searchable library (same slot dimensions apply)
                                  </TooltipContent>
                                </Tooltip>
                                {asset.image_url ? (
                                  <span
                                    className="text-xs text-muted-foreground truncate max-w-[220px]"
                                    title={asset.image_url}
                                  >
                                    Selected:{' '}
                                    {asset.image_url.length > 52
                                      ? `${asset.image_url.slice(0, 52)}…`
                                      : asset.image_url}
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">
                                    Pick an image sized for {spec.label}.
                                  </span>
                                )}
                              </div>
                            )}
                            {asset.source === 'device' && (
                              <div className="flex flex-wrap items-center gap-2">
                                <input
                                  ref={el => {
                                    customGameFileInputsRef.current[asset.id] = el;
                                  }}
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={onCustomGameFileSelected(asset.id, asset.image_type)}
                                />
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      disabled={isUploading}
                                      onClick={() =>
                                        customGameFileInputsRef.current[asset.id]?.click()
                                      }
                                    >
                                      {isUploading ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      ) : (
                                        <Upload className="mr-2 h-4 w-4" />
                                      )}
                                      Upload from device
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    File must match {spec.width}×{spec.height} or 2×
                                  </TooltipContent>
                                </Tooltip>
                                {asset.image_url ? (
                                  <span className="text-xs text-muted-foreground truncate max-w-[220px]">
                                    {asset.image_url}
                                  </span>
                                ) : null}
                              </div>
                            )}

                            {asset.image_url ? (
                              <div className="h-28 w-full max-w-[200px] rounded border bg-muted/20 overflow-hidden">
                                <img
                                  src={asset.image_url}
                                  alt=""
                                  className="h-full w-full object-contain"
                                  onError={e => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap justify-end gap-2 border-t pt-4">
                    <Button type="button" variant="outline" onClick={goToCustomGameList}>
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleSaveCustomGame}
                      disabled={isSavingCustomGame}
                    >
                      {isSavingCustomGame
                        ? editingCustomGameId
                          ? 'Saving...'
                          : 'Creating...'
                        : editingCustomGameId
                          ? 'Save changes'
                          : 'Create custom game'}
                    </Button>
                  </div>
                </ContentCard>
              </>
            )}
          </TabsContent>

          <TabsContent value="asset-settings" className="mt-0">
            <TenantLobbyAssetSettingsPanel tenantCode={tenantCode} />
          </TabsContent>
        </Tabs>

        <Dialog
          open={!!libraryTarget || !!customGameLibraryTarget}
          onOpenChange={open => {
            if (!open) {
              setLibraryTarget(null);
              setCustomGameLibraryTarget(null);
              setLibrarySearch('');
            }
          }}
        >
          <DialogContent className="max-w-5xl p-0">
            <DialogHeader className="px-6 pt-6">
              <DialogTitle>Select Image from Library</DialogTitle>
              <DialogDescription>
                {libraryTarget
                  ? 'Choose an image from tenant image library for this lobby image slot.'
                  : customGameLibraryTarget
                    ? `Choose an image sized for ${IMAGE_SPECS[customGameLibraryTarget.imageType].label}: ${IMAGE_SPECS[customGameLibraryTarget.imageType].width}×${IMAGE_SPECS[customGameLibraryTarget.imageType].height} (or 2×).`
                    : 'Choose an image from tenant image library.'}
              </DialogDescription>
            </DialogHeader>

            <div className="px-6 pb-6 space-y-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={librarySearch}
                    onChange={e => setLibrarySearch(e.target.value)}
                    placeholder="Search images by filename..."
                    className="pl-9"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => refetchImageLibrary()}
                  disabled={isImageLibraryFetching}
                >
                  <RefreshCcw
                    className={isImageLibraryFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
                  />
                  Refresh
                </Button>
              </div>

              <div className="max-h-[58vh] overflow-y-auto border rounded-md p-3">
                {isImageLibraryLoading ? (
                  <div className="py-10 text-center text-sm text-muted-foreground">
                    Loading image library...
                  </div>
                ) : isImageLibraryError ? (
                  <div className="py-10 text-center text-sm text-red-600">
                    Failed to load image library.
                  </div>
                ) : filteredLibraryImages.length === 0 ? (
                  <div className="py-10 text-center text-sm text-muted-foreground">
                    {(tenantImageLibrary?.images?.length ?? 0) === 0
                      ? 'No images found in this tenant image library.'
                      : 'No images match your search.'}
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {filteredLibraryImages.map(image => {
                      const displayName = getDisplayName(image.name);
                      return (
                        <button
                          key={image.name}
                          type="button"
                          className="text-left border rounded-md p-2 hover:border-primary transition-colors"
                          onClick={() => onSelectImageFromLibrary(image.url)}
                        >
                          <div className="h-28 w-full overflow-hidden rounded border bg-muted/20 mb-2">
                            <img
                              src={image.url}
                              alt={displayName}
                              className="h-full w-full object-contain"
                              loading="lazy"
                            />
                          </div>
                          <div className="text-xs font-medium truncate" title={displayName}>
                            {displayName}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatFileSize(image.size)}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <AlertDialog
          open={!!activationConfirm}
          onOpenChange={open => {
            if (!open) setActivationConfirm(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Make this lobby active?</AlertDialogTitle>
              <AlertDialogDescription>
                {activationConfirm ? (
                  <>
                    Are you sure you want to make{' '}
                    <span className="font-semibold">{activationConfirm.title}</span> active? All
                    other lobbies with the slug{' '}
                    <span className="font-mono">{activationConfirm.slug}</span> for this tenant will
                    become inactive. Only one lobby per slug can be active at a time.
                  </>
                ) : null}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  if (!activationConfirm) return;
                  try {
                    await patchLobbyMutation.mutateAsync({
                      lobbyId: activationConfirm.id,
                      body: { is_active: true },
                    });
                    toast.success(
                      `"${activationConfirm.title}" is now active for slug "${activationConfirm.slug}".`
                    );
                  } catch (error) {
                    toast.error(
                      (error as { message?: string })?.message ?? 'Failed to activate lobby'
                    );
                  } finally {
                    setActivationConfirm(null);
                  }
                }}
              >
                Make active
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!toDelete} onOpenChange={open => !open && setToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete lobby</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete "{toDelete?.title}" and remove every game association.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  if (!toDelete) return;
                  try {
                    await deleteMutation.mutateAsync(toDelete.id);
                    toast.success(`Deleted "${toDelete.title}"`);
                    setToDelete(null);
                  } catch (error) {
                    const message =
                      error instanceof Error ? error.message : 'Failed to delete lobby';
                    toast.error(message);
                  }
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </PageContainer>
    </TooltipProvider>
  );
}
